import { Injectable, Renderer2 } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  retry,
  switchMap,
  take,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { NodesApiService, TreeBaseNode } from '@alfresco/adf-content-services';
import {
  Node,
  NodeEntry,
  RequestPagination,
  ResultSetPaging,
  SearchRequest,
  NodePaging,
} from '@alfresco/js-api';
import find from 'lodash/find';

import { SiteConfigService } from './site-config.service';
import { WkContentApiService } from './wk-content-api.service';
import { FolderOperations } from '../types';
import { WkAlfrescoApiService } from './wk-alfresco-api.service';
import { WkAppConfigService } from './wk-app-config.service';
import { CommonConstants } from '../shared';
import { MediaTypeExtensions, MediaTypes } from '../enums';
import { ContentService } from '../components/shared/services/content.service';
import {
  currentSiteSelector,
  getAppSelection,
} from '../store/selectors/app.custom-selectors';
import { AppStore } from '../store/states/app.state';

const { SELECTED_CHILD } = CommonConstants;

@Injectable({
  providedIn: 'root',
})
export class WkTreeViewService {
  private readonly folderOperations$ = new Subject<FolderOperations>();
  private readonly mediasFolder$ = new BehaviorSubject<Node | null>(null);
  private readonly idsToSites$ = new BehaviorSubject<string[]>([]);
  private readonly mediaType$ = new Subject<MediaTypes>();

  constructor(
    private readonly nodeApiService: NodesApiService,
    private readonly contentApiService: WkContentApiService,
    private readonly siteConfigService: SiteConfigService,
    private readonly apiService: WkAlfrescoApiService,
    private readonly appConfigService: WkAppConfigService,
    private readonly siteConfig: SiteConfigService,
    private readonly contentService: ContentService,
    private readonly matSnackBar: MatSnackBar,
    private readonly store: Store<AppStore>
  ) {}

  public getMediasFolder(): Observable<Node | null> {
    return this.mediasFolder$.asObservable();
  }

  public getIdsToSites(): Observable<string[]> {
    return this.idsToSites$.asObservable();
  }

  public setMediaType(mediaType: MediaTypes): void {
    this.mediaType$.next(mediaType);
  }

  // Method to get the ids to the Sites folder from the medias folder or current folder
  public loadIdsToSites(): void {
    this.getPathToMediasFolder()
      .pipe(
        distinctUntilChanged(),
        switchMap((pathToMediasFolder) =>
          pathToMediasFolder
            ? // If the path to the medias folder is provided, search for the medias folder in the current site
              this.fetchMediasFolder(pathToMediasFolder)
            : this.getCurrNode()
        ),
        map((node) => {
          this.mediasFolder$.next(node);
          if (node) {
            const slicePathToSites = this.slicePathToSites(node);
            return node.isFolder
              ? slicePathToSites.concat(node.id)
              : slicePathToSites;
          }

          return [];
        })
      )
      .subscribe((ids) => this.idsToSites$.next(ids));
  }

  public resetMediasFolder(): void {
    this.mediasFolder$.next(null);
  }

  private getPathToMediasFolder(): Observable<string> {
    return this.mediaType$.pipe(
      switchMap((mediaType) => {
        const currSiteConfig = this.siteConfig.getCurrentSiteObject(
          localStorage.getItem('currentSite')
        );
        const pathToMediasFolder =
          currSiteConfig.media[mediaType.toLocaleLowerCase()]?.fts_path;
        return of(pathToMediasFolder);
      })
    );
  }

  private fetchMediasFolder(
    pathToMediasFolder: string
  ): Observable<Node | null> {
    return this.contentService.getMediaFolder(pathToMediasFolder).pipe(
      retry(3),
      map((data) =>
        data?.list?.entries?.length ? data.list.entries[0].entry : null
      ),
      catchError((error) => {
        this.matSnackBar.open('Something went wrong', 'Close');
        return throwError(() => error);
      })
    );
  }

  private getCurrNode(): Observable<Node | null> {
    return this.store
      .select(getAppSelection)
      .pipe(
        map((selection) =>
          selection.nodes.length ? selection.nodes[0].entry : null
        )
      );
  }

  // Method to slice the path to the Sites folder
  private slicePathToSites(node: Node): string[] {
    return node.path.elements
      .slice(node.path.elements.findIndex((folder) => folder.name === 'Sites'))
      .map((elementPath) => elementPath.id);
  }

  getTreeNodes(nodeId: any): Observable<TreeBaseNode[]> {
    return this.getCustomTreeNodes(nodeId);
  }

  getCustomNodes(nodeId: string): Observable<NodeEntry[]> {
    return this.nodeApiService
      .getNodeChildren(nodeId, {
        maxItems: 1000,
        include: ['aspectNames', 'properties', 'path'],
      })
      .pipe(
        map((nodePage: NodePaging) =>
          nodePage.list.entries.filter(
            (node) =>
              node.entry.isFolder ||
              (node.entry.isFile &&
                !node.entry.aspectNames.includes('cm:workingcopy') &&
                node.entry.content &&
                node.entry.aspectNames.indexOf('rn:hiddenRendition') === -1)
          )
        )
      );
  }

  getCustomTreeNodes(
    nodeId: any,
    specificType = '',
    forbiddenType = ''
  ): Observable<TreeBaseNode[]> {
    return this.nodeApiService
      .getNodeChildren(nodeId, {
        maxItems: 1000,
        include: ['aspectNames', 'properties', 'path'],
      })
      .pipe(
        map((nodePage: NodePaging) =>
          nodePage.list.entries.filter((node) => {
            if (specificType) {
              const re = new RegExp(specificType, 'g');
              return node.entry.isFolder ||
                (node.entry.isFile &&
                  node.entry.aspectNames.indexOf('cm:workingcopy') === -1 &&
                  node.entry.content &&
                  node.entry.content.mimeType.match(re) &&
                  !this.hasForbiddenType(node, forbiddenType) &&
                  node.entry.aspectNames.indexOf('rn:hiddenRendition') === -1)
                ? node
                : null;
            } else {
              return node.entry.isFolder;
            }
          })
        ),
        map((nodes: NodeEntry[]) => nodes.map((node) => new TreeBaseNode(node)))
      );
  }

  getNodeChildren(
    nodeId: string = null,
    fileName: string = null
  ): Observable<ResultSetPaging> {
    return this.store.select(currentSiteSelector).pipe(
      take(1),
      switchMap((fetchCurrentSite: string) => {
        const currentSiteConfig = this.getCurrentSiteConfig(fetchCurrentSite);
        const contentNodeType = currentSiteConfig?.documentNodeType?.type;
        const mediaNodeType = currentSiteConfig?.mediaNodeType?.type;

        const query: SearchRequest = {
          ...this.getQuery(mediaNodeType, contentNodeType, nodeId, fileName),
          include: ['path', 'properties', 'aspectNames'],
        };

        return this.apiService.searchApi.search(query);
      })
    );
  }

  private getCurrentSiteConfig(fetchCurrentSite: string): any {
    const sitesConfigs = this.appConfigService.get('sites');
    return find(sitesConfigs, { id: fetchCurrentSite });
  }

  private getQuery(
    mediaType: string,
    contentType: string,
    nodeId: string,
    fileName: string
  ) {
    if (nodeId) {
      return {
        query: {
          query: `PARENT:'workspace://SpacesStore/${nodeId}' AND (TYPE: 'cm:folder' OR TYPE:'${contentType}' OR (TYPE:'${mediaType}' AND =cm:content.mimetype:'application/pdf'))`,
          language: 'afts',
        },
        paging: {
          maxItems: 1000,
        },
        filterQueries: [
          {
            query: `NOT ASPECT:'cm:workingcopy'`,
          },
        ],
      };
    }
    if (fileName) {
      return {
        query: {
          query: `=cm:name:'${fileName}*'`,
          language: 'afts',
        },
        paging: {
          maxItems: 1000,
        },
        filterQueries: [
          {
            query: `TYPE:'${contentType}' OR (TYPE:'${mediaType}' AND =cm:content.mimetype:'application/pdf')`,
          },
          {
            query: `NOT ASPECT:'cm:workingcopy'`,
          },
        ],
      };
    }
    return null;
  }

  getSpecificFiles(
    specificType = '',
    fileName = '',
    siteId = '',
    paging: RequestPagination = {
      maxItems: 10000,
      skipCount: 0,
    }
  ): Observable<ResultSetPaging> {
    const nodeType = this.siteConfigService.getNodeTypeForSearch(
      siteId,
      specificType
    );
    const query: SearchRequest = {
      query: {
        query: `cm:name:"${fileName}*"`,
        language: 'afts',
      },
      paging,
      include: ['path', 'properties'],
      filterQueries: [
        { query: `TYPE:'${nodeType}'` },
        { query: `cm:content.mimetype:'${specificType}/*'` },
      ],
    };
    return this.contentApiService.search(query);
  }

  getSpecificDocuments(
    specificType = '',
    forbiddenType = '',
    fileName = '',
    siteId = '',
    searchType = ''
  ): Observable<ResultSetPaging> {
    const arrayTypes = specificType.split('|');
    const nodeType = this.siteConfigService.getNodeTypeForSearch(
      siteId,
      searchType
    );
    const query: SearchRequest = {
      query: {
        query: `cm:name:"${fileName}*"`,
        language: 'afts',
      },
      filterQueries: [
        { query: `TYPE:'${nodeType}'` },
        {
          query: `NOT cm:content.mimetype: '${forbiddenType}*' AND (cm:content.mimetype:'${arrayTypes[0]}/*' OR cm:content.mimetype:'${arrayTypes[1]}/*')`,
        },
        { query: `-ASPECT:'cm:workingcopy'` },
      ],
      include: ['aspectNames', 'properties'],
    };
    return this.contentApiService.search(query);
  }

  private hasForbiddenType(node, forbiddenType) {
    if (forbiddenType) {
      const forbiddenRex = new RegExp(forbiddenType, 'g');
      return node.entry.content.mimeType.match(forbiddenRex);
    }
    return false;
  }

  getFolderOperation(): Observable<FolderOperations> {
    return this.folderOperations$.asObservable();
  }

  triggerTreeViewReload(folderOeration: FolderOperations) {
    this.folderOperations$.next(folderOeration);
  }

  public getSelectedImageNodes(event, node, renderer: Renderer2): any[] {
    let selectedMediaNodes = [];
    const hasClass = event.target
      .closest('.child')
      .classList?.contains(SELECTED_CHILD);
    if (hasClass) {
      renderer.removeClass(event.target.closest('.child'), SELECTED_CHILD);
      selectedMediaNodes = selectedMediaNodes.filter(
        (eachnode) => node.entry.id !== eachnode.entry.id
      );
    } else {
      renderer.addClass(event.target.closest('.child'), SELECTED_CHILD);
      selectedMediaNodes.push(node);
    }
    return selectedMediaNodes;
  }

  public getExtensions(mediaType: MediaTypes): string[] {
    return mediaType ? MediaTypeExtensions.get(mediaType) : [];
  }
}
